/* eslint-disable no-shadow */
import { setupApollo } from '../../lib/apollo_setup';
import { PREPOPULATE_RESERVATION_QUERY } from '@/graphql/backup_care/queries';

const apolloProvider = setupApollo();
interface BackupCareState {
  selectedSitterUUID: string;
  invitedSitterEmail: string;
  isBusinessSitter: boolean;
  invitedSitterData: {
    businessInfo?: {
      businessName?: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
    };
  };
  careSession: {
    id: string;
    careType: string;
    start: string;
    end: string;
    location: {
      details: string;
    };
    individualCareRecipientDetails: {
      endDateTime: string;
      startDateTime: string;
      firstName: string;
      status: string;
    };
    bhReservationRequest: {
      bhReservationRequestId: string;
    };
  };
  careSessionFetchError: boolean;
}

const initialState: BackupCareState = {
  selectedSitterUUID: '',
  invitedSitterEmail: '',
  isBusinessSitter: false,
  invitedSitterData: {
    businessInfo: {
      businessName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
    },
  },
  careSession: {
    id: '',
    careType: '',
    start: '',
    end: '',
    location: {
      details: '',
    },
    individualCareRecipientDetails: {
      endDateTime: '',
      startDateTime: '',
      firstName: '',
      status: '',
    },
    bhReservationRequest: {
      bhReservationRequestId: '',
    },
  },
  careSessionFetchError: false,
};

const state: BackupCareState = { ...initialState };

const mutations = {
  SET_INVITED_SITTER_EMAIL(state, email) {
    // Ensure only invitedSitterEmail is set by clearing selectedSitterUUID
    state.invitedSitterEmail = email;
    state.selectedSitterUUID = '';
  },
  SET_SELECTED_SITTER_UUID(state, uuid) {
    // Ensure only selectedSitterUUID is set by clearing invitedSitterEmail
    state.selectedSitterUUID = uuid;
    state.invitedSitterEmail = '';
  },
  SET_IS_BUSINESS_SITTER(state, isBusinessSitter) {
    state.isBusinessSitter = isBusinessSitter;
  },
  SET_SITTER_BUSINESS_INFO(state, businessInfo) {
    state.invitedSitterData.businessInfo = { ...businessInfo };
  },
  SET_INVITED_SITTER_DATA(state, data) {
    state.invitedSitterData = { ...state.invitedSitterData, ...data };
  },
  SET_CARE_SESSION(state, careSession) {
    state.careSession = careSession;
  },
  SET_CARE_SESSION_FETCH_ERROR(state, hasError) {
    state.careSessionFetchError = hasError;
  },
  RESET_CARE_SESSION(state) {
    state.careSession = { ...initialState.careSession };
    state.careSessionFetchError = initialState.careSessionFetchError;
  },
};

const actions = {
  setInvitedSitterEmail({ commit }, email) {
    commit('SET_INVITED_SITTER_EMAIL', email);
  },
  setSelectedSitterUUID({ commit }, uuid) {
    commit('SET_SELECTED_SITTER_UUID', uuid);
  },
  setIsBusinessSitter({ commit }, isBusinessSitter) {
    commit('SET_IS_BUSINESS_SITTER', isBusinessSitter);
  },
  setInvitedSitterData({ commit }, data) {
    commit('SET_INVITED_SITTER_DATA', data);
  },
  setSitterBusinessInfo({ commit }, data) {
    commit('SET_SITTER_BUSINESS_INFO', { ...data });
  },
  setCareSession({ commit }, careSession) {
    commit('SET_CARE_SESSION', careSession);
  },
  setCareSessionFetchError({ commit }, hasError) {
    commit('SET_CARE_SESSION_FETCH_ERROR', hasError);
  },
  resetCareSession({ commit }) {
    commit('RESET_CARE_SESSION');
  },
  async mostRecentActiveReservation() {
    try {
      const { data } = await apolloProvider.defaultClient.query({
        query: PREPOPULATE_RESERVATION_QUERY,
      });

      const reservations = data?.corp?.backupCare?.bhReservationRequests;
      const currentDateISO = new Date().toISOString();
      const findMostRecentActiveReservation: any = reservations.reduce((mostRecentReservation: any, reservation: any) => {
        const startDateExpired: boolean = reservation.expired < currentDateISO;
        const isMoreRecentSession: boolean =
          reservation.createdAt > mostRecentReservation?.createdAt || mostRecentReservation === null;
        // check if the session is valid start time and more recent
        if (!startDateExpired && isMoreRecentSession) {
          return reservation;
        }
        return mostRecentReservation;
      }, null);

      return findMostRecentActiveReservation;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching reservations', error);
      return null;
    }
  },
};

const getters = {
  invitedSitterEmail: (state: BackupCareState) => state.invitedSitterEmail,
  selectedSitterUUID: (state: BackupCareState) => state.selectedSitterUUID,
  isBusinessSitter: (state: BackupCareState) => state.isBusinessSitter,
  invitedSitterData: (state: BackupCareState) => state.invitedSitterData,
  careSession: (state: BackupCareState) => state.careSession,
  careSessionFetchError: (state: BackupCareState) => state.careSessionFetchError,
};

export default {
  state,
  mutations,
  actions,
  getters,
};
